import { SxProps, Theme } from "@mui/material";
import templatesTokens, {
  PHONE_SM_QUERY,
  templatesResponsiveTokens,
} from "./Templates.tokens";

const {
  page,
  breadcrumb,
  header,
  viewToggle,
  primaryButton,
  secondaryButton,
  toolbar,
  grid,
  card,
  list,
  row,
  kebab,
  empty,
  footer,
  freshState,
} = templatesTokens;

const { phone, tablet } = templatesResponsiveTokens;

/** Row grid shared by the list header and every list row so columns line up. */
const nameButtonReset = {
  display: "block",
  width: "100%",
  m: 0,
  p: 0,
  border: 0,
  background: "transparent",
  fontFamily: "inherit",
  textAlign: "start" as const,
  cursor: "inherit",
  outline: "none",
};

const nameButtonRing = (inset: string, radius: string, offset: string) => ({
  "&::after": {
    content: '""',
    position: "absolute" as const,
    inset,
    borderRadius: radius,
    pointerEvents: "none" as const,
  },
  "&:focus-visible::after": {
    outline: "2px solid",
    outlineColor: "primary.main",
    outlineOffset: offset,
  },
});

const listGrid = {
  display: "grid",
  gridTemplateColumns: {
    xs: "minmax(0,1fr) auto",
    md: list.gridTemplateColumns,
  },
  gap: list.gap,
  alignItems: "center",
} as const;

const templatesStyles = {
  /**
   * `compact` drops the page gutters for the embedded "pick a template" modal,
   * which supplies its own dialog padding.
   *
   * Below `md` the reference lays the page out edge to edge with its own
   * gutters (14/16px on the phone, 26px on the tablet), so the shared
   * `CenteredAuthenticatedLayout` gutter and top margin are pulled back off
   * with negative margins first and the reference values applied on top.
   * From `md` up nothing changes — the desktop screen keeps the layout's
   * gutters plus its own 40px column.
   */
  page: (compact = false) =>
    (compact
      ? {
          display: "flex",
          flexDirection: "column",
          gap: { xs: phone.gap.std, sm: tablet.gap, md: 0 },
          pt: 0,
          px: 0,
          pb: 0,
        }
      : {
          // Below `md` the reference spaces the blocks with one column gap
          // instead of the per-block bottom margins the desktop screen uses.
          display: "flex",
          flexDirection: "column",
          gap: { xs: phone.gap.std, sm: tablet.gap, md: 0 },
          mt: { xs: "-16px", sm: "-32px", md: 0 },
          mx: { xs: "-16px", sm: "-24px", md: 0 },
          pt: {
            xs: phone.paddingTop.std,
            sm: tablet.paddingTop,
            md: page.paddingTop,
          },
          px: {
            xs: phone.paddingX.std,
            sm: tablet.paddingX,
            md: page.paddingX,
          },
          pb: {
            xs: phone.paddingBottom.std,
            sm: tablet.paddingBottom,
            md: page.paddingBottom,
          },
          [PHONE_SM_QUERY]: {
            gap: phone.gap.sm,
            paddingTop: phone.paddingTop.sm,
            paddingLeft: phone.paddingX.sm,
            paddingRight: phone.paddingX.sm,
            paddingBottom: phone.paddingBottom.sm,
          },
        }) as SxProps<Theme>,

  breadcrumb: {
    display: "flex",
    alignItems: "center",
    // The phone reference runs the trail as one scrolling line rather than
    // letting it wrap; the tablet keeps the desktop's wrapping row.
    flexWrap: { xs: "nowrap", sm: "wrap" },
    overflowX: { xs: "auto", sm: "visible" },
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
    // Bleeds into the page gutter so the first crumb still lines up with the
    // cards while the rest can scroll past the edge.
    mx: { xs: `-${phone.paddingX.std}`, sm: 0 },
    px: { xs: phone.paddingX.std, sm: 0 },
    gap: { xs: phone.breadcrumb.gap, sm: breadcrumb.gap },
    /*
      No bottom margin here. This style is spread onto the `<ol>`, which then
      sets `m: 0` to drop the browser's default list margin — that reset also
      ate this one, so the trail sat flush against the toolbar. The page-level
      spacing lives on the wrapping `<nav>` instead; see `breadcrumbNav`.
    */
    [PHONE_SM_QUERY]: {
      marginLeft: `-${phone.paddingX.sm}`,
      marginRight: `-${phone.paddingX.sm}`,
      paddingLeft: phone.paddingX.sm,
      paddingRight: phone.paddingX.sm,
    },
  } as SxProps<Theme>,

  /**
   * The trail's wrapper, and the element that actually carries its spacing —
   * it is the page column's direct child, so its margin is the gap the eye
   * reads. Matches `toolbar.marginBottom` so the trail sits the same distance
   * above the search row as the search row sits above the list. Below `md` the
   * page column's own `gap` does the spacing, so it stays at 0 there.
   */
  breadcrumbNav: (atRoot: boolean) =>
    ({
      display: { xs: atRoot ? "none" : "block", md: "block" },
      mb: { xs: 0, md: breadcrumb.marginBottom },
    }) as SxProps<Theme>,

  breadcrumbItem: (isLast: boolean, disabled: boolean) =>
    ({
      display: "inline-flex",
      alignItems: "center",
      gap: "7px",
      p: 0,
      /*
        A crumb is a `Button`, and the theme gives every button a 40px height
        (see `button.height`). Zeroing the padding is not enough — without this
        the trail reads as a 40px band around one line of text.
      */
      height: "auto",
      minHeight: 0,
      minWidth: 0,
      flexShrink: 0,
      whiteSpace: "nowrap",
      border: "none",
      background: "none",
      fontSize: {
        xs: phone.breadcrumb.fontSize.std,
        sm: tablet.breadcrumbFontSize,
        md: breadcrumb.fontSize,
      },
      fontWeight: breadcrumb.fontWeight,
      lineHeight: "normal",
      textTransform: "none",
      color: isLast ? breadcrumb.activeColor : breadcrumb.linkColor,
      cursor: disabled ? "not-allowed" : isLast ? "default" : "pointer",
      "&:hover": { background: "none", textDecoration: "none" },
      [PHONE_SM_QUERY]: { fontSize: phone.breadcrumb.fontSize.sm },
    }) as SxProps<Theme>,

  breadcrumbSeparator: {
    // Phone swaps the chevron for a plain "/" — see `breadcrumbSlash`.
    display: { xs: "none", sm: "inline-block" },
    width: { sm: tablet.breadcrumbSeparatorSize, md: breadcrumb.separatorSize },
    height: {
      sm: tablet.breadcrumbSeparatorSize,
      md: breadcrumb.separatorSize,
    },
    color: breadcrumb.separatorColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  /** Phone trail uses a plain "/" instead of the chevron glyph. */
  breadcrumbSlash: {
    display: { xs: "inline", sm: "none" },
    flexShrink: 0,
    color: phone.breadcrumb.separatorColor,
    fontSize: phone.breadcrumb.fontSize.std,
    fontWeight: breadcrumb.fontWeight,
    lineHeight: "normal",
    [PHONE_SM_QUERY]: { fontSize: phone.breadcrumb.fontSize.sm },
  } as SxProps<Theme>,

  /**
   * `hideTitle` (the modal-embedded picker, which gets its title from
   * `AppModal` instead) drops the title block, leaving only the actions —
   * `space-between` with a single remaining child lands it at the start, not
   * the end, so the row right-aligns explicitly instead.
   */
  header: (hideTitle = false) =>
    ({
      display: "flex",
      alignItems: { xs: "center", sm: "flex-end" },
      justifyContent: hideTitle ? "flex-end" : "space-between",
      flexDirection: "row",
      gap: { xs: "12px", sm: header.gap },
      mb: { xs: 0, md: header.marginBottom },
    }) as SxProps<Theme>,

  /**
   * The reference puts the screen name in the app bar on a phone, but this
   * app's app bar is shared by every screen and its right-hand cluster leaves
   * no room for a title at 375 — so the title stays in the page body and takes
   * the reference's app-bar type scale (16 / 16.5px, weight 800) instead.
   */
  headerTitle: {
    m: 0,
    mb: { xs: "4px", sm: 0 },
    color: header.titleColor,
    fontSize: {
      xs: phone.titleFontSize.std,
      sm: tablet.titleFontSize,
      md: header.titleFontSize,
    },
    fontWeight: header.titleFontWeight,
    letterSpacing: { sm: header.titleLetterSpacing },
    lineHeight: 1.2,
    [PHONE_SM_QUERY]: { fontSize: phone.titleFontSize.sm },
  } as SxProps<Theme>,

  headerSubtitle: {
    m: 0,
    mt: { xs: 0, sm: header.subtitleMarginTop },
    color: header.subtitleColor,
    fontSize: {
      xs: phone.subtitleFontSize.std,
      sm: tablet.subtitleFontSize,
      md: header.subtitleFontSize,
    },
    lineHeight: { xs: phone.subtitleLineHeight, sm: 1.4 },
    textWrap: "pretty",
    [PHONE_SM_QUERY]: { fontSize: phone.subtitleFontSize.sm },
  } as SxProps<Theme>,

  /**
   * The strapline ships twice. 375 has no room for the square Create button,
   * so it runs the full sentence over two lines; 390 shortens it and puts the
   * button on the same row. Tablet and desktop take the long copy again.
   */
  headerSubtitleLong: {
    m: 0,
    mt: { xs: 0, sm: header.subtitleMarginTop },
    color: header.subtitleColor,
    fontSize: {
      xs: phone.subtitleFontSize.std,
      sm: tablet.subtitleFontSize,
      md: header.subtitleFontSize,
    },
    lineHeight: { xs: phone.subtitleLineHeight, sm: 1.4 },
    textWrap: "pretty",
    display: { xs: "none", sm: "block" },
    [PHONE_SM_QUERY]: {
      display: "block",
      fontSize: phone.subtitleFontSize.sm,
    },
  } as SxProps<Theme>,

  headerSubtitleShort: {
    m: 0,
    color: header.subtitleColor,
    fontSize: phone.subtitleFontSize.std,
    lineHeight: phone.subtitleLineHeight,
    textWrap: "pretty",
    display: { xs: "block", sm: "none" },
    [PHONE_SM_QUERY]: { display: "none" },
  } as SxProps<Theme>,

  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: { sm: tablet.headerActionsGap, md: header.gap },
    flexShrink: 0,
    // 375 has nothing to put here — the toggle is gone and Create floats — so
    // the row must not reserve a gap for an empty box.
    [PHONE_SM_QUERY]: { display: "none" },
  } as SxProps<Theme>,

  /** Grid / list toggle — the phone reference drops it entirely. */
  viewToggle: {
    display: { xs: "none", sm: "flex" },
    bgcolor: viewToggle.bg,
    border: `1px solid ${viewToggle.border}`,
    borderRadius: viewToggle.radius,
    p: viewToggle.padding,
    flexShrink: 0,
  } as SxProps<Theme>,

  viewToggleButton: (active: boolean) =>
    ({
      width: viewToggle.buttonWidth,
      height: viewToggle.buttonHeight,
      minWidth: viewToggle.buttonWidth,
      p: 0,
      borderRadius: viewToggle.buttonRadius,
      color: active
        ? viewToggle.activeColor
        : { sm: tablet.viewToggleInactiveColor, md: viewToggle.color },
      bgcolor: active ? viewToggle.activeBg : "transparent",
      "&:hover": {
        bgcolor: active ? viewToggle.activeBg : "transparent",
      },
      "&.Mui-disabled": {
        color: active ? viewToggle.activeColor : viewToggle.color,
        bgcolor: active ? viewToggle.activeBg : "transparent",
        opacity: 0.6,
      },
      "&.Mui-focusVisible": {
        outline: "2px solid",
        outlineColor: "primary.main",
        outlineOffset: "-2px",
      },
    }) as SxProps<Theme>,

  /** Main half of the "Create" split button. */
  /*
   * The "Create Request" split button carries no screen styling — see
   * `palette.splitButton` and CustomDropdownButton.
   */

  /**
   * Phone "Create" — one full-width button under the header, at every phone
   * size.
   *
   * The reference drew a 40px square beside the subtitle at 390 and floated a
   * pill over the list at 375. The pill covered the rows it was meant to sit
   * beside, and the square gave no label, so both are replaced by the same
   * full-width button the Dashboard and Sign Requests use.
   */
  createMobile: {
    display: { xs: "flex", sm: "none" },
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    gap: phone.createButton.gap,
    p: phone.createButton.padding,
    borderRadius: phone.createButton.radius,
    background: primaryButton.bg,
    color: primaryButton.color,
    fontSize: phone.createButton.fontSize,
    fontWeight: primaryButton.fontWeight,
    lineHeight: "normal",
    textTransform: "none",
    whiteSpace: "nowrap",
    boxShadow: "none",
    "&:hover": { background: primaryButton.hoverBg, boxShadow: "none" },
    "&.Mui-disabled": { background: primaryButton.bg, opacity: 0.5 },
  } as SxProps<Theme>,

  secondaryButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "9px",
    minWidth: 0,
    width: { xs: "100%", sm: "auto" },
    height: secondaryButton.height,
    p: secondaryButton.padding,
    bgcolor: secondaryButton.bg,
    border: `1px solid ${secondaryButton.border}`,
    borderRadius: {
      xs: phone.freshState.buttonRadius,
      sm: secondaryButton.radius,
    },
    color: secondaryButton.color,
    fontSize: secondaryButton.fontSize,
    fontWeight: secondaryButton.fontWeight,
    lineHeight: "normal",
    textTransform: "none",
    whiteSpace: "nowrap",
    boxShadow: "none",
    "&:hover": {
      bgcolor: secondaryButton.bg,
      borderColor: secondaryButton.hoverBorder,
      color: secondaryButton.hoverColor,
      boxShadow: "none",
    },
  } as SxProps<Theme>,

  toolbar: {
    display: "flex",
    alignItems: "center",
    flexDirection: { xs: "column", sm: "row" },
    gap: toolbar.gap,
    mb: { xs: 0, md: toolbar.marginBottom },
  } as SxProps<Theme>,

  searchField: {
    display: "flex",
    alignItems: "center",
    flex: 1,
    width: "100%",
    minWidth: 0,
    gap: { xs: phone.search.gap, sm: tablet.search.gap, md: toolbar.searchGap },
    bgcolor: toolbar.bg,
    border: `1px solid ${toolbar.border}`,
    borderRadius: toolbar.radius,
    p: {
      xs: phone.search.padding,
      sm: tablet.search.padding,
      md: toolbar.padding,
    },
    boxSizing: "border-box",
    "&:focus-within": { borderColor: toolbar.focusBorder },
  } as SxProps<Theme>,

  searchInput: {
    flex: 1,
    minWidth: 0,
    "& .MuiInputBase-input": {
      p: 0,
      height: "auto",
      fontSize: {
        xs: phone.search.inputFontSize.std,
        sm: tablet.search.inputFontSize,
        md: toolbar.inputFontSize,
      },
      fontWeight: toolbar.inputFontWeight,
      lineHeight: "normal",
      color: toolbar.inputColor,
      "&::placeholder": {
        color: toolbar.placeholderColor,
        opacity: 1,
        fontWeight: toolbar.inputFontWeight,
      },
      [PHONE_SM_QUERY]: { fontSize: phone.search.inputFontSize.sm },
    },
  } as SxProps<Theme>,

  /**
   * Tracks are `minmax(0,1fr)`, not `1fr`.
   *
   * `1fr` is shorthand for `minmax(auto,1fr)`, and that `auto` floor resolves to
   * the track's **min-content** width. The cards below set `white-space: nowrap`
   * on the name, sub-label and "Created …" lines, so a card's min-content width
   * is the full untruncated template name — which meant one long name widened
   * its track, widened the grid past the page, and scrolled the whole document.
   * (The `ellipsis` never engaged either, because the name was never actually
   * constrained.) A `0` floor lets each track take its equal share and hands the
   * truncation back to the text. Same idiom as `listGrid` above.
   */
  grid: {
    display: "grid",
    gridTemplateColumns: {
      xs: "minmax(0,1fr)",
      sm: `repeat(${tablet.grid.columns},minmax(0,1fr))`,
      md: "repeat(3,minmax(0,1fr))",
      lg: `repeat(${grid.columns},minmax(0,1fr))`,
    },
    gap: { xs: phone.list.gap, sm: tablet.grid.gap, md: grid.gap },
  } as SxProps<Theme>,

  card: (disabled: boolean) =>
    ({
      position: "relative",
      // The card is a grid item, which also defaults to `min-width: auto` —
      // the track may now shrink, but without this the card itself would still
      // refuse to go below its longest nowrap line and spill out of its track.
      minWidth: 0,
      bgcolor: card.bg,
      border: `1px solid ${card.border}`,
      borderRadius: card.radius,
      boxShadow: card.shadow,
      p: { sm: tablet.card.padding, md: card.padding },
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.6 : 1,
      textAlign: "start",
      "&:hover": {
        borderColor: card.hoverBorder,
        boxShadow: card.hoverShadow,
      },
    }) as SxProps<Theme>,

  cardHead: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    mb: { sm: tablet.card.headMarginBottom, md: card.headMarginBottom },
  } as SxProps<Theme>,

  cardIconTile: (bg: string) =>
    ({
      width: { sm: tablet.card.iconTileSize, md: card.iconTileSize },
      height: { sm: tablet.card.iconTileSize, md: card.iconTileSize },
      borderRadius: card.iconTileRadius,
      bgcolor: bg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }) as SxProps<Theme>,

  cardKebabButton: {
    width: card.kebabSize,
    height: card.kebabSize,
    p: 0,
    borderRadius: card.kebabRadius,
    color: kebab.color,
    "&:hover": { bgcolor: kebab.hoverBg, color: kebab.hoverColor },
  } as SxProps<Theme>,

  cardName: {
    fontSize: { sm: tablet.card.nameFontSize, md: card.nameFontSize },
    fontWeight: card.nameFontWeight,
    color: card.nameColor,
    lineHeight: 1.35,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  } as SxProps<Theme>,

  cardOpenButton: {
    position: "absolute",
    inset: "-1px",
    m: 0,
    p: 0,
    border: 0,
    background: "transparent",
    borderRadius: card.radius,
    pointerEvents: "none",
    cursor: "inherit",
    outline: "none",
    "&:focus-visible": {
      outline: "2px solid",
      outlineColor: "primary.main",
      outlineOffset: "2px",
    },
  } as SxProps<Theme>,

  cardSub: {
    mt: { sm: tablet.card.subMarginTop, md: card.subMarginTop },
    fontSize: { sm: tablet.card.subFontSize, md: card.subFontSize },
    color: card.subColor,
    lineHeight: 1.35,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  } as SxProps<Theme>,

  cardCreated: {
    mt: { sm: tablet.card.createdMarginTop, md: card.createdMarginTop },
    fontSize: { sm: tablet.card.createdFontSize, md: card.createdFontSize },
    color: card.createdColor,
    lineHeight: 1.35,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  } as SxProps<Theme>,

  /**
   * "Created by …" is a desktop-only line. The reference card at 834 shows the
   * name, the item count and a single "Created on" stamp.
   */
  cardCreatedBy: {
    display: { xs: "none", md: "block" },
    mt: card.createdMarginTop,
    fontSize: card.createdFontSize,
    color: card.createdColor,
    lineHeight: 1.35,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  } as SxProps<Theme>,

  listCard: {
    bgcolor: list.bg,
    border: `1px solid ${list.border}`,
    borderRadius: list.radius,
    boxShadow: list.shadow,
    overflow: "hidden",
  } as SxProps<Theme>,

  listHead: {
    ...listGrid,
    p: list.headPadding,
    borderBottom: `1px solid ${list.divider}`,
    fontSize: list.headFontSize,
    fontWeight: list.headFontWeight,
    letterSpacing: list.headLetterSpacing,
    color: list.headColor,
    textTransform: "uppercase",
    display: { xs: "none", md: "grid" },
  } as SxProps<Theme>,

  listRow: (disabled: boolean) =>
    ({
      ...listGrid,
      position: "relative",
      p: { sm: tablet.row.padding, md: row.padding },
      borderBottom: `1px solid ${list.divider}`,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.6 : 1,
      "&:last-of-type": { borderBottom: "none" },
      "&:hover": { bgcolor: row.hoverBg },
    }) as SxProps<Theme>,

  rowLead: {
    display: "flex",
    alignItems: "center",
    gap: row.leadGap,
    minWidth: 0,
  } as SxProps<Theme>,

  rowIconTile: (bg: string) =>
    ({
      width: row.iconTileSize,
      height: row.iconTileSize,
      borderRadius: row.iconTileRadius,
      bgcolor: bg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }) as SxProps<Theme>,

  rowName: {
    fontSize: row.nameFontSize,
    fontWeight: row.nameFontWeight,
    color: row.nameColor,
    lineHeight: 1.35,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  } as SxProps<Theme>,

  rowNameButton: {
    fontSize: row.nameFontSize,
    fontWeight: row.nameFontWeight,
    color: row.nameColor,
    lineHeight: 1.35,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    ...nameButtonReset,
    ...nameButtonRing("0px", "0px", "-2px"),
  } as SxProps<Theme>,

  rowSub: {
    mt: row.subMarginTop,
    fontSize: row.subFontSize,
    color: row.subColor,
    lineHeight: 1.35,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  } as SxProps<Theme>,

  rowType: {
    fontSize: row.typeFontSize,
    fontWeight: row.typeFontWeight,
    color: row.typeColor,
    lineHeight: "normal",
    display: { xs: "none", md: "block" },
  } as SxProps<Theme>,

  rowCreatedBy: {
    fontSize: row.createdFontSize,
    color: row.createdColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: { xs: "none", md: "block" },
  } as SxProps<Theme>,

  rowCreated: {
    fontSize: row.createdFontSize,
    color: row.createdColor,
    lineHeight: "normal",
    display: { xs: "none", md: "block" },
  } as SxProps<Theme>,

  kebabColumn: {
    display: "flex",
    justifyContent: "center",
    flexShrink: 0,
  } as SxProps<Theme>,

  kebabButton: {
    width: kebab.size,
    height: kebab.size,
    p: 0,
    borderRadius: kebab.radius,
    color: kebab.color,
    "&:hover": { bgcolor: kebab.hoverBg, color: kebab.hoverColor },
  } as SxProps<Theme>,

  /* ------------------------------------------------------------------ */
  /* Phone card — the reference collapses grid *and* list to one compact  */
  /* row below `sm`, so the view toggle has nothing to switch down there. */
  /* ------------------------------------------------------------------ */

  mobileList: {
    display: "flex",
    flexDirection: "column",
    gap: phone.list.gap,
  } as SxProps<Theme>,

  mobileCard: (disabled: boolean) =>
    ({
      display: "flex",
      alignItems: "center",
      // Grid item, so the same `min-width: auto` floor applies. The text column
      // inside already carries `minWidth: 0`, but that only helps once the card
      // itself is allowed to be narrower than its longest line.
      minWidth: 0,
      gap: phone.card.gap.std,
      bgcolor: card.bg,
      border: `1px solid ${card.border}`,
      borderRadius: phone.card.radius,
      p: phone.card.padding.std,
      position: "relative",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.6 : 1,
      textAlign: "start",
      [PHONE_SM_QUERY]: {
        gap: phone.card.gap.sm,
        padding: phone.card.padding.sm,
      },
    }) as SxProps<Theme>,

  mobileIconTile: (bg: string) =>
    ({
      width: phone.card.iconTileSize.std,
      height: phone.card.iconTileSize.std,
      borderRadius: phone.card.iconTileRadius,
      bgcolor: bg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      [PHONE_SM_QUERY]: {
        width: phone.card.iconTileSize.sm,
        height: phone.card.iconTileSize.sm,
      },
    }) as SxProps<Theme>,

  mobileName: {
    fontSize: phone.card.nameFontSize.std,
    fontWeight: card.nameFontWeight,
    color: card.nameColor,
    lineHeight: 1.35,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [PHONE_SM_QUERY]: { fontSize: phone.card.nameFontSize.sm },
  } as SxProps<Theme>,

  mobileNameButton: {
    fontSize: phone.card.nameFontSize.std,
    fontWeight: card.nameFontWeight,
    color: card.nameColor,
    lineHeight: 1.35,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [PHONE_SM_QUERY]: { fontSize: phone.card.nameFontSize.sm },
    ...nameButtonReset,
    ...nameButtonRing("-1px", phone.card.radius, "2px"),
  } as SxProps<Theme>,

  mobileSub: {
    mt: phone.card.subMarginTop,
    fontSize: phone.card.subFontSize.std,
    color: row.subColor,
    lineHeight: 1.35,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [PHONE_SM_QUERY]: { fontSize: phone.card.subFontSize.sm },
  } as SxProps<Theme>,

  mobileKebabButton: {
    width: phone.card.kebabSize.std,
    height: phone.card.kebabSize.std,
    flexShrink: 0,
    p: 0,
    borderRadius: phone.card.kebabRadius.std,
    color: kebab.color,
    "&:hover": { bgcolor: kebab.hoverBg, color: kebab.hoverColor },
    [PHONE_SM_QUERY]: {
      width: phone.card.kebabSize.sm,
      height: phone.card.kebabSize.sm,
      borderRadius: phone.card.kebabRadius.sm,
    },
  } as SxProps<Theme>,

  emptyCard: {
    bgcolor: empty.bg,
    border: `1px solid ${empty.border}`,
    borderRadius: { xs: phone.empty.radius, md: empty.radius },
    p: { xs: phone.empty.padding.std, sm: empty.padding },
    textAlign: "center",
    [PHONE_SM_QUERY]: { padding: phone.empty.padding.sm },
  } as SxProps<Theme>,

  emptyTitle: {
    fontSize: { xs: phone.empty.titleFontSize.std, sm: empty.titleFontSize },
    fontWeight: { xs: phone.empty.titleFontWeight, sm: empty.titleFontWeight },
    color: empty.titleColor,
    lineHeight: "normal",
    [PHONE_SM_QUERY]: { fontSize: phone.empty.titleFontSize.sm },
  } as SxProps<Theme>,

  emptySub: {
    mt: empty.subMarginTop,
    fontSize: { xs: phone.empty.subFontSize.std, sm: empty.subFontSize },
    color: empty.subColor,
    lineHeight: "normal",
    [PHONE_SM_QUERY]: { fontSize: phone.empty.subFontSize.sm },
  } as SxProps<Theme>,

  footer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: { xs: phone.footer.gap, sm: footer.gap },
    mt: { xs: phone.footer.marginTop, md: footer.marginTop },
  } as SxProps<Theme>,

  footerLoading: {
    display: "flex",
    alignItems: "center",
    gap: { xs: phone.footer.gap, sm: "10px" },
    fontSize: {
      xs: phone.footer.loadingFontSize.std,
      sm: footer.loadingFontSize,
    },
    fontWeight: footer.loadingFontWeight,
    color: footer.loadingColor,
    lineHeight: "normal",
    [PHONE_SM_QUERY]: { fontSize: phone.footer.loadingFontSize.sm },
  } as SxProps<Theme>,

  footerSpinner: {
    color: footer.spinnerColor,
  } as SxProps<Theme>,

  footerAtEnd: {
    fontSize: footer.atEndFontSize,
    color: footer.atEndColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  footerShown: {
    fontSize: { xs: phone.footer.shownFontSize.std, sm: footer.shownFontSize },
    color: footer.shownColor,
    lineHeight: "normal",
    [PHONE_SM_QUERY]: { fontSize: phone.footer.shownFontSize.sm },
  } as SxProps<Theme>,

  freshIcon: {
    width: { xs: phone.freshState.iconSize.std, sm: freshState.iconSize },
    height: { xs: phone.freshState.iconSize.std, sm: freshState.iconSize },
    borderRadius: "50%",
    bgcolor: freshState.iconBg,
    color: freshState.iconStroke,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mb: {
      xs: phone.freshState.iconMarginBottom.std,
      sm: freshState.iconMarginBottom,
    },
    [PHONE_SM_QUERY]: {
      width: phone.freshState.iconSize.sm,
      height: phone.freshState.iconSize.sm,
      marginBottom: phone.freshState.iconMarginBottom.sm,
    },
  } as SxProps<Theme>,

  freshTitle: {
    fontSize: {
      xs: phone.freshState.titleFontSize.std,
      sm: tablet.freshState.titleFontSize,
      md: freshState.titleFontSize,
    },
    fontWeight: freshState.titleFontWeight,
    color: freshState.titleColor,
    lineHeight: "normal",
    textAlign: "center",
    [PHONE_SM_QUERY]: { fontSize: phone.freshState.titleFontSize.sm },
  } as SxProps<Theme>,

  freshBody: {
    mt: freshState.bodyMarginTop,
    maxWidth: freshState.bodyMaxWidth,
    fontSize: {
      xs: phone.freshState.bodyFontSize.std,
      sm: freshState.bodyFontSize,
    },
    color: freshState.bodyColor,
    lineHeight: {
      xs: phone.freshState.bodyLineHeight,
      sm: freshState.bodyLineHeight,
    },
    textAlign: "center",
    [PHONE_SM_QUERY]: { fontSize: phone.freshState.bodyFontSize.sm },
  } as SxProps<Theme>,

  freshActions: {
    display: "flex",
    // The phone reference stacks the two actions full width; the tablet and
    // desktop sit them side by side.
    flexDirection: { xs: "column", sm: "row" },
    alignItems: { xs: "stretch", sm: "center" },
    width: { xs: "100%", sm: "auto" },
    gap: { xs: phone.freshState.actionsGap, sm: freshState.actionsGap },
    mt: {
      xs: phone.freshState.actionsMarginTop.std,
      sm: freshState.actionsMarginTop,
    },
    [PHONE_SM_QUERY]: { marginTop: phone.freshState.actionsMarginTop.sm },
  } as SxProps<Theme>,

  freshPrimaryButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "9px",
    height: freshState.buttonHeight,
    minWidth: 0,
    width: { xs: "100%", sm: "auto" },
    p: freshState.buttonPadding,
    background: primaryButton.bg,
    borderRadius: {
      xs: phone.freshState.buttonRadius,
      sm: primaryButton.radius,
    },
    color: primaryButton.color,
    fontSize: primaryButton.fontSize,
    fontWeight: primaryButton.fontWeight,
    lineHeight: "normal",
    textTransform: "none",
    whiteSpace: "nowrap",
    boxShadow: "none",
    "&:hover": { background: primaryButton.hoverBg, boxShadow: "none" },
  } as SxProps<Theme>,
};

export default templatesStyles;
